import React, {useEffect, useState} from 'react';
import {cn} from '@/utils';
import {TileListComponent} from '@/components';
import {selectInstance, instancesSelector, updateInstanceMatrix} from '../slices/instanceSlice';
import {overwriteState as overwriteMatrixState, matrixSelector, fetchChangeOptions} from '../slices/matrixSlice';
import {overwriteState as overwriteAppState} from '../slices/appSlice';
import {useDispatch, useSelector} from 'react-redux';
import RollIcon from '@/Icons/instances/icon_label_rolls.svg';
import SheetIcon from '@/Icons/instances/icon_label_sheets.svg';
import StickerIcon from '@/Icons/instances/icon_label_stickers.svg';
import StickerIconAU from '@/Icons/instances/icon_label_stickers_AU.svg';
import BusinessCardIcon from '@/Icons/instances/icon_business_card.svg';
import FoldedCardIcon from '@/Icons/instances/icon_folded_card.svg';
import PostCardIcon from '@/Icons/instances/icon_postcard.svg';
import TagsIcon from '@/Icons/instances/icon_tags.svg';
import DustCoverIcon from '@/Icons/instances/icon_dust_cover.svg';
import OutdoorLabelsIcon from '@/Icons/instances/icon_roll_foil.svg';
import PhotoPaperIcon from '@/Icons/instances/icon_photo_paper.svg';
import FabricTransfer from '@/Icons/instances/icon_fabric_transfer.svg';
import RollRibbon from '@/Icons/instances/icon_roll_ribbon.svg';
import FloorStickers from '@/Icons/instances/icon_floor_stickers.svg';

const createBundleTypes = (items) => {
  const options = [
    { type: 'printing', label: Drupal.t('Let us print for you') },
    { type: 'label', label: Drupal.t('You print yourself') },
  ];

  // Find the first occurrence index of each option type in items
  const getFirstIndex = (optionType) => {
    return items.findIndex(item => item.state.app.bundle.includes(optionType));
  };

  return options
    .filter(option => getFirstIndex(option.type) !== -1)
    .sort((a, b) => getFirstIndex(a.type) - getFirstIndex(b.type));
};

const VISIBLE_PRODUCT_TYPES = 6; // 2 rows × 3 per row

const Instances = ({instances, isFetching, settings}) => {
  const dispatch = useDispatch();
  const matrix = useSelector(matrixSelector);

  const translation = {productType: Drupal.t('Product Type')};
  const startsWithAu = instances.some(item => item.state?.matrix?.defaultConfig?.startsWith('au-'));
  const {groupByType = false} = settings;
  const [bundleNames] = useState(createBundleTypes(instances));
  const [selectedBundle, setSelectedBundle] = useState(bundleNames[0].type);

  useEffect(() => {
    const selectedIndex = instances.findIndex((i) => i.is_selected === true);
    if (instances[selectedIndex].state.matrix.fetched === false) {
      dispatch(updateInstanceMatrix(matrix));
    }
  }, [matrix.lastUpdate]);


  const handleSelectInstance = (index) => {
    const isGrouped = groupByType === true && bundleNames.length > 1;
    const isParagraph = instances[0].state.app.isParagraph === true;

    const newAppState = { ...instances[index].state.app };
    if (isGrouped) {
      if (isParagraph) {
        newAppState.headline = '';
        newAppState.subHeadline = '';
      } else {
        delete newAppState.headline;
        delete newAppState.subHeadline;
      }
    }

    dispatch(overwriteAppState(newAppState));
    dispatch(selectInstance({ index }));

    if (instances[index].state.matrix.fetched === false) {
      const { productId, defaultConfig, media, pageOptionsHide, optionsOverwrite , pageOptionsFilter, priceListStyle, denyCustomFormats } = instances[index].state.matrix;
      // Don't update media here — pass it through the thunk so
      // handleOptionsResponse can set media + apply rules atomically.
      dispatch(
        fetchChangeOptions({
          variables: {
            productId,
            defaultConfig,
            pageOptionsHide,
            pageOptionsFilter,
            priceListStyle,
            denyCustomFormats,
            optionsOverwrite: optionsOverwrite ? optionsOverwrite : {},
          },
          isInitialRequest: true,
          instanceMedia: media,
        }),
      );
    } else {
      dispatch(overwriteMatrixState(instances[index].state.matrix));
    }
  };

  const productTypeItems = groupByType === true && bundleNames.length > 1
    ? instances.map((item, index) => ({ ...item, orgIndex: index })).filter((item) => item.state.app.bundle.includes(selectedBundle))
    : instances.map((item, index) => ({ ...item, orgIndex: index }));

  return (
    <>
      <If condition={groupByType === true && bundleNames.length > 1}>
        <TileListComponent
          tileClassName={cn('h-[5rem]', 'w-1/' + Math.min(bundleNames.length, 2))}
          headline={Drupal.t('You choose:')}
          items={bundleNames.map(bundleName => ({ label: bundleName.label, is_selected: bundleName.type === selectedBundle }))}
          onItemClick={
            ({ index }) => {
              if (bundleNames[index].type !== selectedBundle) {
                const firstItemIndex = instances.findIndex((item) => item.state.app.bundle.includes(bundleNames[index].type));
                handleSelectInstance(firstItemIndex);
                setSelectedBundle(bundleNames[index].type);
              }
            }
          }
        />
      </If>
      <TileListComponent
        id='product-type'
        tileClassName={cn('h-[5rem]', 'w-1/' + Math.max(2, Math.min(productTypeItems.length, 3)))}
        headline={Drupal.t('Choose your @item:', {'@item': translation.productType})}
        items={productTypeItems}
        getItemDataAttributes={(item) => ({'data-product-type': item.state?.matrix?.baseSku})}
        onItemClick={(t) => handleSelectInstance(t.orgIndex)}
        LoadingIcon={() => (
          <ft-spinner size="small"></ft-spinner>
        )}
        isDisabled={isFetching}
        showIcon
        showLabel
        reduceItems={productTypeItems.length > VISIBLE_PRODUCT_TYPES}
        persistReduceState
        reduceItemsText={Drupal.t("Show fewer product types")}
        expandItemsText={Drupal.t("Show more product types")}
        initialSelectId={productTypeItems.find(item => item.is_selected)?.id}
        dynamicItemClassNames={({i, selectedIndex, itemsReduced}) => ([
          i >= VISIBLE_PRODUCT_TYPES && i !== selectedIndex && itemsReduced ? "hidden" : "",
          i === VISIBLE_PRODUCT_TYPES - 1 && selectedIndex >= VISIBLE_PRODUCT_TYPES && itemsReduced ? "hidden" : "",
        ])}
        Icon={(t) => {
          const attributes = {
            role: 'img',
            'aria-labelledby': 'title',
            title: t.label,
            className: 'w-4xl h-4xl m-3xs',
          };

          const getIcon = (id) => {
            switch (id) {
              case '1':
              case '3':
              case '23':
                return <SheetIcon {...attributes} />;
                break;
              case '4':
              case '35':
                return <TagsIcon {...attributes} />;
                break;
              case '5':
                return <PhotoPaperIcon {...attributes} />;
                break;
              case '9':
                return <RollRibbon {...attributes} />;
                break;
              case '20':
              case '33':
              case '39':
                return <BusinessCardIcon {...attributes} />;
                break;
              case '21':
              case '40':
                return <FoldedCardIcon {...attributes} />;
                break;
              case '22':
              case '41':
                return <PostCardIcon {...attributes} />;
                break;
              case '7':
              case '8':
              case '10':
              case '24':
              case '42':
                return <RollIcon {...attributes} />;
                break;
              case '26':
                return startsWithAu ? <StickerIconAU {...attributes} /> : <StickerIcon {...attributes} />;
                break;
              case '27':
                return <OutdoorLabelsIcon {...attributes} />;
                break;
              case '28':
                return <FloorStickers {...attributes} />;
                break;
              case '34':
                return <FabricTransfer {...attributes} />;
                break;
              case '36':
                return <DustCoverIcon {...attributes} />;
                break;
              case '38':
                return <BusinessCardIcon {...attributes} />;
                break;
            }
            return null;
          }

          const icon = getIcon(t.id);

          if (icon) {
            return (
              <span
                className="flex items-center flex-col absolute inset-y-0 laptop:flex-row laptop:gap-4xs">
              {icon}
                <If condition={t.showLabel}>
                <span className="text-center inline-block break-normal">
                  {t.label}
                </span>
              </If>
            </span>
            );
          }
          return null;
        }}
      />
    </>
  );
};
export default Instances;
